import { useRef, useState } from 'react';
import api from 'api';
import { actions } from 'store';
import { useDispatch, useReduxState } from 'hooks';
import { Button, ModalButtonWithForm } from 'components/common';
import {
  ArrayField,
  Code,
  DisplayField,
  LanguageVariants,
  Text
} from 'components/common/form';

import type { FormApi } from 'informed';
import type { UserCustomAttribute } from 'shared-types';

interface Props {
  attribute: UserCustomAttribute;
  optionGroupIndex: number;
  updateData: () => Promise<void>;
}

const UserAttributesOptionsAdd = ({
  attribute,
  optionGroupIndex,
  updateData
}: Props) => {
  const { dispatch } = useDispatch();
  const { language, messages, user } = useReduxState();

  const [mode, setMode] = useState<'default' | 'paste'>('default');

  const formApi = useRef<FormApi>(undefined);

  const optionGroup = attribute.optionGroups[optionGroupIndex];

  const existingLabels = {};
  const existingKeys = {};
  optionGroup.options.forEach((option) => {
    existingLabels[option.label] = true;
    existingKeys[option.key] = true;
  });

  const handleKeyDown = (add, event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (event.target.value?.trim()) {
        const nextInput =
          event.target.parentElement?.parentElement?.parentElement?.nextSibling
            ?.firstChild?.firstChild?.firstChild;

        if (nextInput) {
          nextInput.focus();
        } else {
          add();

          setTimeout(() => {
            const nextInput =
              event.target.parentElement?.parentElement?.parentElement
                ?.nextSibling?.firstChild?.firstChild?.firstChild;
            if (nextInput) nextInput.focus();
          });
        }
      }
    }
  };

  const handleKeyBlur = (index: number) => {
    const currentValue = (formApi.current.getValue(
      `newOptions[${index}].key`
    ) || '') as string;
    formApi.current.setValue(`newOptions[${index}].key`, currentValue.trim());
  };

  const handleLabelBlur = (index: number) => {
    const currentValue = (formApi.current.getValue(
      `newOptions[${index}].label`
    ) || '') as string;
    formApi.current.setValue(`newOptions[${index}].label`, currentValue.trim());
  };

  const handleLabelChange = (index: number, value: string) => {
    if (optionGroup.enableKeys) {
      formApi.current.setValue(`newOptions[${index}].key`, value);
    } else {
      // ensure new key is unique
      const newOptions = formApi.current.getValue('newOptions') as {
        key: string;
      }[];
      const existingNewKeys = {};
      newOptions.forEach((option, optionIndex) => {
        if (option.key && optionIndex !== index) {
          existingNewKeys[option.key] = true;
        }
      });

      let newKey = value;
      let iterator = 0;
      while (existingKeys[newKey] || existingNewKeys[newKey]) {
        iterator += 1;
        newKey = `${value}${iterator}`;
      }

      formApi.current.setValue(`newOptions[${index}].key`, newKey);
    }
  };

  const handleSubmit = async (newOptions) => {
    let options = [...optionGroup.options, ...newOptions];

    if (optionGroup.sort === 'alphabetical') {
      options = options.sort((a, b) =>
        a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1
      );
    }

    const data = {
      optionGroups: [
        ...attribute.optionGroups.slice(0, optionGroupIndex),
        { ...optionGroup, options },
        ...attribute.optionGroups.slice(optionGroupIndex + 1)
      ]
    };

    await api.put(`/admin/user-custom-attributes/${attribute.id}`, data);

    await updateData();

    dispatch(
      actions.addNotice({
        text: messages.commonChangesSavesSuccessfully,
        type: 'success'
      })
    );
  };

  const handleSubmitDefault = async (values) => {
    await handleSubmit(values.newOptions);
  };

  const handleSubmitPaste = async (values) => {
    const newOptions = (values.pastedOptions || '')
      .replaceAll('\r\n', '\n')
      .replaceAll('\r', '\n')
      .split('\n')
      .map((option) => {
        const trimmedOption = option.trim();
        return {
          key: trimmedOption,
          label: trimmedOption
        };
      });

    await handleSubmit(newOptions);
  };

  const validateOptions = (newOptions) => {
    const existingNewKeys = {};
    const existingNewLabels = {};
    newOptions.forEach((option) => {
      if (option.key) {
        existingNewKeys[option.key] = (existingNewKeys[option.key] || 0) + 1;
      }
      if (option.label) {
        existingNewLabels[option.label] =
          (existingNewLabels[option.label] || 0) + 1;
      }
    });

    newOptions.forEach((option, optionIndex) => {
      let keyError = undefined;
      let labelError = undefined;

      if (!option.key?.trim()) {
        keyError = messages.commonKeyEmpty;
      } else if (existingKeys[option.key]) {
        keyError = messages.commonKeyInUse;
      } else if (existingNewKeys[option.key] > 1) {
        keyError = messages.commonKeyInUse;
      }

      if (!option.label?.trim()) {
        labelError = messages.commonTitleEmpty;
      } else if (existingLabels[option.label]) {
        labelError = messages.commonTitleInUse;
      } else if (existingNewLabels[option.label] > 1) {
        labelError = messages.commonTitleInUse;
      }

      formApi.current.setError(`newOptions[${optionIndex}].key`, keyError);
      formApi.current.setError(`newOptions[${optionIndex}].label`, labelError);
    });
  };

  const initialValues = {
    newOptions: [{ key: '', label: '', labelTranslations: {} }]
  };

  return (
    <ModalButtonWithForm
      buttonCategory="success"
      buttonIcon="plus"
      buttonSize="header"
      buttonText={messages.commonAddOptions}
      initialValues={initialValues}
      modalActionText={messages.commonAdd}
      modalTitle={messages.commonAddOptions}
      modalWidth={optionGroup.enableKeys ? 'medium' : undefined}
      onSubmit={mode === 'paste' ? handleSubmitPaste : handleSubmitDefault}
      pristineIsValid={false}
      setFormApi={(form: FormApi) => (formApi.current = form)}
    >
      {() => (
        <div>
          {mode === 'paste' ? (
            <Code
              field="pastedOptions"
              placeholder={`Option 1\nOption 2\nOption 3`}
            />
          ) : (
            // @ts-ignore
            (<ArrayField field="newOptions" validate={validateOptions}>
              {({ add }) => (
                <>
                  <div className="header">
                    <div className="header-title">{messages.commonTitle}</div>
                    {optionGroup.enableKeys ? (
                      <div className="header-title">
                        {optionGroup.customKeyNameTranslations?.[language] ||
                          optionGroup?.customKeyName ||
                          messages.attributesKey}
                      </div>
                    ) : null}
                  </div>
                  <div className="options">
                    <ArrayField.Items<{ isExisting: boolean; key: string }>>
                      {({ field: itemField, index, key, remove, values }) => (
                        <div className="option">
                          <div className="label">
                            <Text
                              field={`${itemField}.label`}
                              infoMessage={
                                optionGroup.enableKeys
                                  ? undefined
                                  : `${
                                      optionGroup.customKeyNameTranslations?.[
                                        language
                                      ] ||
                                      optionGroup?.customKeyName ||
                                      messages.attributesKey
                                    }: ${values?.key || ''}`
                              }
                              onBlur={handleLabelBlur.bind(null, index)}
                              onKeyDown={handleKeyDown.bind(null, add)}
                              onValueChange={handleLabelChange.bind(
                                null,
                                index
                              )}
                              placeholder={messages.commonTitle}
                            />

                            {optionGroup.enableTranslations ? (
                              <LanguageVariants
                                field={`${itemField}.labelTranslations`}
                                type="text"
                              />
                            ) : null}
                          </div>

                          <div
                            className="key"
                            style={{
                              display: optionGroup.enableKeys ? 'block' : 'none'
                            }}
                          >
                            <Text
                              disabled={values?.isExisting}
                              field={`${itemField}.key`}
                              onBlur={handleKeyBlur.bind(null, index)}
                              placeholder={
                                optionGroup.customKeyNameTranslations?.[
                                  language
                                ] ||
                                optionGroup?.customKeyName ||
                                messages.attributesKey
                              }
                            />
                          </div>

                          <DisplayField
                            field={`${itemField}.uuid`}
                            initialValue={key}
                          />

                          <div className="delete">
                            <Button
                              aria-label={messages.commonDelete}
                              category="light"
                              data-tooltip={messages.commonDelete}
                              icon="trash-alt"
                              onClick={remove}
                              size="small"
                            />
                          </div>
                        </div>
                      )}
                    </ArrayField.Items>
                  </div>

                  <div className="add">
                    <Button
                      category="light"
                      icon="plus"
                      onClick={add}
                      size="small"
                      text={messages.commonAddOption}
                    />
                  </div>
                </>
              )}
            </ArrayField>)
          )}

          {user?.roles?.includes('vuoadmin') ? (
            <div className="mode">
              {mode === 'default' ? (
                <Button
                  category="light"
                  onClick={() => setMode('paste')}
                  size="small"
                  text={'Paste data instead'}
                />
              ) : (
                <Button
                  category="light"
                  onClick={() => setMode('default')}
                  size="small"
                  text={'Add individual options instead'}
                />
              )}
            </div>
          ) : null}

          <style jsx>{`
            .header {
              display: flex;
              gap: 10px;
              margin-bottom: 5px;
              padding-right: 38px;
            }
            .header-title {
              flex-basis: 50%;
              flex-grow: 1;
            }
            .options {
              display: flex;
              flex-direction: column;
              gap: 10px;
            }
            .option {
              display: flex;
              gap: 10px;
            }
            .label {
              flex-grow: 1;
            }
            .key {
              flex-grow: 1;
            }
            .delete {
              flex-grow: 0;
              flex-shrink: 0;
              padding-top: 3px;
            }
            .add {
              margin-top: 10px;
            }
            .mode {
              margin-top: 40px;
            }
          `}</style>
        </div>
      )}
    </ModalButtonWithForm>
  );
};

export default UserAttributesOptionsAdd;
